import PropTypes from "prop-types"

import useAuth from "minimals-template/components/@hooks/useAuth"
import LoadingScreen from "minimals-template/components/LoadingScreen"

import PasswordlessLogin from "minimals-template/pages/auth/PasswordlessLogin"
import { useEffect } from "react"
import { isMobileApp } from "routes/@layouts/app/header/is-mobile-app"
import { AuthenticatedAuthGuard } from "minimals-template/components/guards/authenticated-auth-guard"
import { useLoginMountReady } from "minimals-template/components/guards/use-login-mount-ready"
import { useLoadingTimeout } from "minimals-template/components/guards/use-loading-timeout"

// FIQ-578: the boot AppRefresh remount normally fires within ~1s; if it never
// arrives we mount the login form anyway rather than spin forever
const LOGIN_MOUNT_WAIT_CAP_MS = 10000

AuthGuard.propTypes = {
    children: PropTypes.node,
}

export default function AuthGuard({ children }) {
    const user = useAuth()
    const loginMountReady = useLoginMountReady()
    const loginMountWaitExpired = useLoadingTimeout(!loginMountReady, LOGIN_MOUNT_WAIT_CAP_MS)

    useEffect(() => {
        if (!isMobileApp()) return () => {}
        document.documentElement.addEventListener("focusin", scrollIn)
        return () => {
            document.documentElement.removeEventListener("focusin", scrollIn)
        }
    }, [])

    const { isAuthenticated, isInitialized } = user

    if (!isInitialized) {
        return <LoadingScreen description="Auth Guard" />
    }

    if (!isAuthenticated) {
        // Wait for the one-time boot AppRefresh remount to settle before mounting the login
        // form so it mounts exactly once (see use-login-mount-ready). Show the same
        // LoadingScreen used during initialisation so there is no visible flash.
        if (!loginMountReady && !loginMountWaitExpired) {
            return <LoadingScreen description="Auth Guard" />
        }
        return <PasswordlessLogin />
    }

    return <AuthenticatedAuthGuard>{children}</AuthenticatedAuthGuard>

    function scrollIn({ target }) {
        target?.scrollIntoView({ block: "center", behavior: "smooth" })
    }
}
